/**
 * Helper functions for pricing calculations and ROI metrics
 */

import type { CalculationResults, AccessNowPricing } from '@/types/calculator';

/**
 * Get the total AccessNow cost
 */
export function getAccessNowTotalCost(results: CalculationResults): number {
  return results.accessNowPricing.totalCost;
}

/**
 * Get the pricing breakdown for display purposes
 */
export function getPricingBreakdown(results: CalculationResults): {
  totalCost: number;
  breakdown: AccessNowPricing;
} {
  return {
    totalCost: results.accessNowPricing.totalCost,
    breakdown: results.accessNowPricing,
  };
}

/**
 * Calculate workflow costs for display
 */
export function getWorkflowCosts(results: CalculationResults): {
  credential: number;
  access: number;
  visitor: number;
  total: number;
} {
  const pricing = results.accessNowPricing;
  
  // Get total requests per workflow type from the calculation results
  const credentialRequests = results.workflowSummary.find(w => w.name.toLowerCase().includes('credential'))?.requestsPerYear || 0;
  const accessRequests = results.workflowSummary.find(w => w.name.toLowerCase().includes('access'))?.requestsPerYear || 0;
  const visitorRequests = results.workflowSummary.find(w => w.name.toLowerCase().includes('visitor'))?.requestsPerYear || 0;
  
  const totalRequests = credentialRequests + accessRequests + visitorRequests;
  
  if (totalRequests === 0) {
    return { credential: 0, access: 0, visitor: 0, total: 0 };
  }
  
  // Distribute TOTAL cost (workflows + request packages) proportionally by request volume
  const totalCost = pricing.totalCost;
  
  const credentialCost = (credentialRequests / totalRequests) * totalCost;
  const accessCost = (accessRequests / totalRequests) * totalCost;
  const visitorCost = (visitorRequests / totalRequests) * totalCost;
  
  return {
    credential: Math.round(credentialCost),
    access: Math.round(accessCost),
    visitor: Math.round(visitorCost),
    total: totalCost,
  };
}

/**
 * Get the Net Annual Savings (Business Value Returned - AccessNow annual cost)
 * Positive value = Net Annual Savings, Negative value = Net Investment Cost
 */
export function getNetAnnualSavings(results: CalculationResults): number {
  const totalCost = getAccessNowTotalCost(results);
  return results.businessEfficiencyValue - totalCost;
}

/**
 * Calculate ROI payback timeline when Net Annual Savings are positive
 * Returns payback period in months or null if no positive savings
 * Formula: Cost / Monthly Savings
 */
export function getROIPaybackTimeline(results: CalculationResults): {
  hasPositiveSavings: boolean;
  monthsToPayback: number | null;
  description: string;
} {
  const annualAccessNowCost = getAccessNowTotalCost(results);
  const businessValueReturned = results.businessEfficiencyValue;
  const netAnnualSavings = businessValueReturned - annualAccessNowCost;
  
  // If Net Annual Savings are positive, calculate monthly payback period
  if (netAnnualSavings > 0) {
    const monthlySavings = businessValueReturned / 12;
    const monthsToPayback = Math.ceil(annualAccessNowCost / monthlySavings);
    return {
      hasPositiveSavings: true,
      monthsToPayback,
      description: `Pays for itself in ${monthsToPayback} months`
    };
  }
  
  // No positive savings - investment provides business value but doesn't pay for itself
  return {
    hasPositiveSavings: false,
    monthsToPayback: null,
    description: "Investment provides business value without direct cost savings"
  };
}

/**
 * Calculate multi-year net benefit over analysis period
 */
export function getMultiYearNetBenefit(results: CalculationResults, analysisYears: number = 3): {
  totalBenefit: number;
  annualBreakdown: Array<{
    year: number;
    businessValueReturned: number;
    accessNowCost: number;
    netAnnualSavings: number;
    cumulativeBenefit: number;
  }>;
} {
  const annualAccessNowCost = getAccessNowTotalCost(results);
  const businessValueReturned = results.businessEfficiencyValue;
  
  const annualBreakdown = [];
  let cumulativeBenefit = 0;
  
  for (let year = 1; year <= analysisYears; year++) {
    const netAnnualSavings = businessValueReturned - annualAccessNowCost;
    cumulativeBenefit += netAnnualSavings;
    
    annualBreakdown.push({
      year,
      businessValueReturned,
      accessNowCost: annualAccessNowCost,
      netAnnualSavings,
      cumulativeBenefit
    });
  }
  
  return {
    totalBenefit: cumulativeBenefit,
    annualBreakdown
  };
}

/**
 * Calculate annualized ROI percentage - only return when Net Annual Savings are positive
 * Formula: Net Benefit / Cost × 100
 * Where Net Benefit = Annual Savings - Annual Cost
 */
export function getROIPercentage(results: CalculationResults): number | null {
  const annualAccessNowCost = getAccessNowTotalCost(results);
  const businessValueReturned = results.businessEfficiencyValue;
  const netBenefit = businessValueReturned - annualAccessNowCost;
  
  // Only calculate ROI percentage when there are positive Net Annual Savings
  if (netBenefit > 0 && annualAccessNowCost > 0) {
    return Math.round((netBenefit / annualAccessNowCost) * 100);
  }
  
  return null; // No ROI percentage when no positive savings
}

/**
 * Get net savings amount (same as Net Annual Savings)
 */
export function getNetSavings(results: CalculationResults): number {
  return getNetAnnualSavings(results);
}

/**
 * Check if there are positive Net Annual Savings (Business Value Returned > AccessNow costs)
 */
export function hasPositiveNetAnnualSavings(results: CalculationResults): boolean {
  const totalCost = getAccessNowTotalCost(results);
  return results.businessEfficiencyValue > totalCost;
}

/**
 * DEPRECATED: Use getROIPaybackTimeline instead
 * Calculate payback period in months - kept for backward compatibility
 */
export function getPaybackPeriodMonths(results: CalculationResults): number | null {
  const payback = getROIPaybackTimeline(results);
  return payback.monthsToPayback;
}

// Backward compatibility aliases
export const getYearOneNetCashFlow = getNetAnnualSavings;
export const hasPositiveROI = hasPositiveNetAnnualSavings;
export const manualCostsExceedAccessNow = hasPositiveNetAnnualSavings;
export const getBreakEvenTimeline = getROIPaybackTimeline;
